/**
 * Marketplace helpers: device-local wallet storage, metric formatting, and the
 * countdown used by every auction card.
 *
 * The wallet token never leaves this device — it is the only way the API will let a
 * self-created wallet spend, and there are no accounts by design.
 */

const WALLET_KEY = "outbidfit.market.wallet.v1";

export const readWallet = () => {
    try {
        const raw = window.localStorage.getItem(WALLET_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.id !== "string") return null;
        return { id: parsed.id, token: parsed.token || null, label: parsed.label || "", demo: !!parsed.demo };
    } catch {
        return null;
    }
};

export const saveWallet = (wallet) => {
    try {
        window.localStorage.setItem(WALLET_KEY, JSON.stringify(wallet));
    } catch {
        /* private mode: the wallet stays in memory for this session only */
    }
    return wallet;
};

export const clearWallet = () => {
    try {
        window.localStorage.removeItem(WALLET_KEY);
    } catch {
        /* ignore */
    }
};

/** Wallet auth headers: demo wallets are public sample funds and need no token. */
export const walletHeaders = (wallet) => {
    if (!wallet?.id) return undefined;
    return wallet.token
        ? { "x-wallet-id": wallet.id, "x-wallet-token": wallet.token }
        : { "x-wallet-id": wallet.id };
};

export const compact = (value) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return "0";
    if (n < 1000) return String(n);
    if (n < 1_000_000) return `${n < 10_000 ? (n / 1000).toFixed(1) : Math.round(n / 1000)}K`;
    return `${(n / 1_000_000).toFixed(1)}M`;
};

export const watchTime = (seconds) => {
    const s = Math.max(0, Math.round(Number(seconds || 0)));
    if (!s) return "—";
    const mins = Math.floor(s / 60);
    return `${mins}:${String(s % 60).padStart(2, "0")}`;
};

export const timeLeft = (seconds) => {
    const s = Math.max(0, Math.floor(Number(seconds || 0)));
    if (!s) return "ended";
    if (s >= 86_400) return `${Math.floor(s / 86_400)}d ${Math.floor((s % 86_400) / 3600)}h`;
    if (s >= 3600) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
    if (s >= 60) return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
    return `${s}s`;
};

export const longDate = (iso) => {
    if (!iso) return "—";
    const date = new Date(iso.length <= 10 ? `${iso}T12:00:00Z` : iso);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const statsBadge = (status) => (status === "verified" ? "Verified" : "Claimed");

export const LISTING_STATUS = {
    live: { label: "Live auction", tone: "brand" },
    sold: { label: "Sold — awaiting proof", tone: "heat" },
    unsold: { label: "Ended — no bids", tone: "muted" },
    completed: { label: "Complete — paid out", tone: "brand" },
    refunded: { label: "Refunded to wallet", tone: "muted" },
    disputed: { label: "In dispute", tone: "heat" },
};

export const statusMeta = (status) =>
    LISTING_STATUS[status] || { label: status || "Unknown", tone: "muted" };

export const DEAL_STATUS = {
    awaiting_proof: "Waiting on creator proof",
    proof_submitted: "Proof in — 48h review window open",
    accepted: "Accepted — payout released",
    disputed: "Disputed — funds held by platform",
    refunded: "Refunded to the winner",
};

export const num = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};